import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter, useSearch } from '@tanstack/react-router';
import { useDebounce } from '@r/hooks/use-debounce.ts';
import { isPointsRangeInvalid, parsePointsFromSearch } from '@r/helpers/product-catalog.ts';

type SearchParams = {
  pointsMin?: string | number;
  pointsMax?: string | number;
  [key: string]: unknown;
};

type PointsRangeInput = {
  min: string;
  max: string;
};

const toInputValue = (value?: number) => (value === undefined ? '' : String(value));

/**
 * Gère le filtre de plage de points du catalogue (`pointsMin` / `pointsMax`) et sa persistance dans l'URL.
 * La saisie est debouncée ; une plage incohérente (min > max) n'est pas appliquée.
 */
export const usePointsRangeFilter = () => {
  const { navigate, state } = useRouter();
  const pathname = state.location.pathname;
  const search = useSearch({ strict: false }) as SearchParams;

  const minFromUrl = parsePointsFromSearch(search.pointsMin);
  const maxFromUrl = parsePointsFromSearch(search.pointsMax);

  const [range, setRange] = useState<PointsRangeInput>({
    min: toInputValue(minFromUrl),
    max: toInputValue(maxFromUrl)
  });
  const debouncedRange = useDebounce(range, 500);
  const rangeSourceRef = useRef<'user' | 'url'>('url');

  useEffect(() => {
    const nextMin = toInputValue(minFromUrl);
    const nextMax = toInputValue(maxFromUrl);
    setRange((current) =>
      parsePointsFromSearch(current.min) === minFromUrl &&
      parsePointsFromSearch(current.max) === maxFromUrl
        ? current
        : { min: nextMin, max: nextMax }
    );
    rangeSourceRef.current = 'url';
  }, [minFromUrl, maxFromUrl]);

  const min = parsePointsFromSearch(range.min);
  const max = parsePointsFromSearch(range.max);
  const isInvalid = isPointsRangeInvalid(min, max);

  useEffect(() => {
    if (rangeSourceRef.current !== 'user') return;
    if (debouncedRange !== range) return;

    const debouncedMin = parsePointsFromSearch(debouncedRange.min);
    const debouncedMax = parsePointsFromSearch(debouncedRange.max);
    if (isPointsRangeInvalid(debouncedMin, debouncedMax)) return;
    if (debouncedMin === minFromUrl && debouncedMax === maxFromUrl) return;

    navigate({
      to: pathname,
      search: (prev: SearchParams) => ({
        ...prev,
        pointsMin: debouncedMin,
        pointsMax: debouncedMax,
        page: 1
      }),
      replace: true
    });
  }, [debouncedRange, range, minFromUrl, maxFromUrl, pathname, navigate]);

  const updateRange = useCallback((bound: keyof PointsRangeInput, value: string) => {
    rangeSourceRef.current = 'user';
    setRange((current) => ({ ...current, [bound]: value }));
  }, []);

  const setMinInput = useCallback((value: string) => updateRange('min', value), [updateRange]);
  const setMaxInput = useCallback((value: string) => updateRange('max', value), [updateRange]);

  const handleResetPointsRange = () => {
    rangeSourceRef.current = 'url';
    setRange({ min: '', max: '' });
    navigate({
      to: pathname,
      search: (prev: SearchParams) => ({
        ...prev,
        pointsMin: undefined,
        pointsMax: undefined,
        page: 1
      }),
      replace: true
    });
  };

  return {
    minInput: range.min,
    maxInput: range.max,
    setMinInput,
    setMaxInput,
    isInvalid,
    hasPointsRange: range.min !== '' || range.max !== '',
    handleResetPointsRange
  };
};
